/**
 * Window-close confirmation for in-flight Multi-Add batches — Multi-Add
 * Phase 2 (PRD §2.10).
 *
 * When the user attempts to close the studio window (Electron quit /
 * browser tab close / refresh) WHILE a bulk-add batch is mid-flight
 * (`bulkAddStatus.kind === 'configuring' | 'committing' | 'preparing' |
 * 'awaiting-auth'`), the renderer intercepts the close attempt and
 * surfaces a confirmation dialog. The user has two choices:
 *   - Confirm close → renderer dies. Devices already committed to the
 *     cloud survive on the agent side via state-sync; the batch tail
 *     never runs. Commits-already-made survive on the cloud side via
 *     the agent's idempotent state.
 *   - Cancel close → batch continues. The renderer stays mounted and
 *     the pipeline proceeds with the next iteration.
 *
 * Copy:
 *   "Close — stop adding sources?" (per Design lead — NOT the
 *   dismissive "Close anyway?" which implies the user already lost the
 *   batch.)
 *
 * Per `architecture-principles` (ai.knowledge/global) — "Electron and
 * Browser are Equal Priorities": this hook wires through the standard
 * `beforeunload` event which Electron's BrowserWindow honors on
 * `window.close()` / Cmd-Q / Alt-F4 and browsers honor on tab close /
 * refresh. No Electron-only path needed.
 *
 * Per `think-in-systems` (ai.knowledge/global): the hook is a pure
 * pub/sub adapter — it has no opinions about the store or the dialog.
 * Callers supply (a) a getter for "is a batch in flight" and (b) a
 * confirmation primitive. Tests pass jest.fn() stubs for both.
 *
 * Per `reference-rules-in-comments` (ai.knowledge/global).
 */
import { useEffect } from 'react';

/**
 * Confirmation copy — exported for test assertion + caller composition.
 *
 * Copy is verbatim: "Close — stop adding sources?".
 * Per Design lead: NOT "Close anyway?".
 */
export const WINDOW_CLOSE_PROMPT = 'Close — stop adding sources?' as const;

/**
 * Browser-canonical `beforeunload` message. Modern browsers IGNORE the
 * returned string for security reasons (anti-spam) and display their
 * own generic prompt, but we still set the property because:
 *   - Setting `event.returnValue` is what actually triggers the native
 *     confirm dialog (the string is the side-effect signal).
 *   - The Electron Renderer's `beforeunload` honors the value verbatim
 *     when the underlying BrowserWindow's `closable` route bypasses
 *     the OS dialog (some Electron versions; behavior varies).
 *
 * For an Electron-app code path that needs the exact copy, a future
 * companion (`window-close-handler` IPC bridge) would replace this with
 * a programmatic Electron `dialog.showMessageBox` — deferred per
 * `stay-in-scope` (ai.knowledge/global): the renderer-level guard is
 * sufficient for browser parity.
 */
const beforeUnloadMessage = WINDOW_CLOSE_PROMPT;

/**
 * Returns true when a bulk-add batch is in a state that should prevent
 * silent window close. Pure predicate so the hook can be exercised
 * outside React (the test suite passes hand-built status objects).
 *
 * With batch in `configuring`/`committing`, browser/electron must
 * confirm. We also include `preparing` (the prep phase has already run
 * any cleanup) and `awaiting-auth` (the user is mid-credential-entry;
 * closing here loses their typed creds + skips the not-yet-attempted
 * tail). `idle` and `done` are safe to close silently.
 *
 * @param status - The current `BulkAddStatus` discriminator from the
 *   zustand store's `source.bulkAddStatus` slice.
 */
export const isBulkAddInFlight = (status: { kind: string }): boolean => {
  return (
    status.kind === 'preparing' ||
    status.kind === 'configuring' ||
    status.kind === 'committing' ||
    status.kind === 'awaiting-auth'
  );
};

/**
 * React hook — wires `beforeunload` to the in-flight predicate.
 *
 * Renders inside the Add modal's scope (mounted by `scaffold.tsx` or a
 * parent route component). The hook subscribes to the bulk-add status,
 * adds the `beforeunload` listener when a batch starts, and removes it
 * when the batch resolves (idle / done) so non-bulk routes don't carry
 * the warning.
 *
 * The listener returns a non-empty string to trigger the browser's
 * native "Leave site?" prompt. Browsers do not honor a custom message
 * (W3C spec change post-Chrome 51), but the prompt itself surfaces and
 * the Confirm/Cancel branches map cleanly:
 *   - Confirm → browser unloads the window (renderer dies).
 *   - Cancel  → browser cancels unload (batch continues).
 *
 * Per `architecture-principles`: browser-first. The Cmd-Q Electron path
 * (which bypasses `beforeunload` on some macOS versions) is deferred
 * to a follow-up — the QA worksheet flags this gap.
 *
 * @param isInFlight - Callback returning `true` while the listener
 *   should be active. Production callers wrap the zustand store
 *   selector (`useStore((s) => isBulkAddInFlight(s.source.bulkAddStatus))`);
 *   tests pass a stable boolean.
 */
export const useWindowCloseGuard = (isInFlight: boolean): void => {
  useEffect(() => {
    if (!isInFlight) return;

    // `beforeunload` is the canonical browser primitive for "interrupt
    // window-close until the user confirms". Both Chrome and Electron's
    // renderer honor it for tab/window close, Cmd-W, browser refresh,
    // navigation away. Returning a non-empty string triggers the native
    // confirm dialog.
    const handler = (event: BeforeUnloadEvent): string => {
      // Standard `returnValue` assignment — required for cross-browser
      // compatibility. The empty-string + assignment pattern is the
      // canonical "trigger native dialog without supplying copy".
      event.preventDefault();
      // Some legacy Chromium variants still display a custom message;
      // setting `returnValue` is the cross-version signal that an
      // unload should be confirmed.
      event.returnValue = beforeUnloadMessage;
      return beforeUnloadMessage;
    };

    window.addEventListener('beforeunload', handler);
    return (): void => {
      window.removeEventListener('beforeunload', handler);
    };
  }, [isInFlight]);
};
